import React, { useEffect, useMemo, useState } from "react";
import {
    Paper,
    Box,
    Typography,
    Stack,
    Chip,
    Divider,
    Button,
    Tooltip,
    Alert,
} from "@mui/material";
import { Save, Lock } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { useGetRoles } from "../../../hooks/roles/useGetRoles";
import { useGetUserRoles } from "../../../hooks/roles/useGetUserRoles";
import { useSyncUserRoles } from "../../../hooks/roles/useSyncUserRoles";
import { useAuthContext } from "../../../auth/useAuthContext";

const ADMIN_ROLE_NAMES = new Set(["admin", "super_admin"]);

/**
 * Manage role assignment for a user. Mirrors the spec rules:
 *   - Built-in admin roles cannot be removed from the active session's user.
 *   - Sync sends `{ role_ids: [] }`.
 */
const CustomerRolesTab = ({ customerId }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const { user } = useAuthContext() || {};
    const isSelf = String(user?.id) === String(customerId);

    const { roles: allRoles } = useGetRoles({ per_page: 200 });
    const { roles: assignedRoles, isLoading } = useGetUserRoles(customerId);
    const { syncUserRolesMutate, isSyncing } = useSyncUserRoles();

    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        if (!Array.isArray(assignedRoles)) return;
        setSelectedIds(
            assignedRoles
                .map((r) => (typeof r === "object" ? r.id : Number(r)))
                .filter(Boolean)
        );
    }, [assignedRoles]);

    const initialIds = useMemo(
        () =>
            (assignedRoles || [])
                .map((r) => (typeof r === "object" ? r.id : Number(r)))
                .filter(Boolean)
                .sort()
                .join(","),
        [assignedRoles]
    );
    const currentIds = useMemo(() => [...selectedIds].sort().join(","), [selectedIds]);
    const dirty = initialIds !== currentIds;

    // const adminRoleIds = useMemo(
    //     () => allRoles.filter((r) => ADMIN_ROLE_NAMES.has(r.name)).map((r) => r.id),
    //     [allRoles]
    // );
    const userHasAdminRole = (assignedRoles || []).some((r) =>
        ADMIN_ROLE_NAMES.has(typeof r === "object" ? r.name : r)
    );

    const toggle = (role) => {
        const id = role.id;
        const wantsToRemoveAdmin =
            isSelf && ADMIN_ROLE_NAMES.has(role.name) && selectedIds.includes(id);
        if (wantsToRemoveAdmin) return;

        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const submit = () => {
        syncUserRolesMutate({
            userId: customerId,
            data: { role_ids: selectedIds },
        });
    };

    const reset = () => {
        setSelectedIds(
            (assignedRoles || [])
                .map((r) => (typeof r === "object" ? r.id : Number(r)))
                .filter(Boolean)
        );
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                spacing={1.5}
                mb={2}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        {isArabic ? "الأدوار المعيّنة" : "Assigned Roles"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic
                            ? "اختر الأدوار التي يحملها هذا المستخدم. التغييرات تُطبَّق عند الحفظ."
                            : "Select the roles this user holds. Changes apply on save."}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                    {dirty && (
                        <Button onClick={reset} disabled={isSyncing}>
                            {isArabic ? "إلغاء" : "Reset"}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={submit}
                        disabled={!dirty || isSyncing}
                        sx={{ borderRadius: 2, fontWeight: "bold" }}
                    >
                        {isSyncing
                            ? (isArabic ? "جاري الحفظ..." : "Saving...")
                            : (isArabic ? "حفظ" : "Save")}
                    </Button>
                </Stack>
            </Stack>

            {isSelf && userHasAdminRole && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {isArabic
                        ? "لا يمكنك إزالة دور المسؤول من حسابك الخاص (حماية ضد القفل الذاتي)."
                        : "You cannot remove your own admin role (anti-lockout protection)."}
                </Alert>
            )}

            <Divider sx={{ mb: 2 }} />

            {isLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                    {isArabic ? "جاري التحميل..." : "Loading..."}
                </Typography>
            ) : allRoles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                    {isArabic ? "لا توجد أدوار متاحة" : "No roles available"}
                </Typography>
            ) : (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {allRoles.map((role) => {
                        const isSelected = selectedIds.includes(role.id);
                        const isAdminRole = ADMIN_ROLE_NAMES.has(role.name);
                        const lockedSelf = isSelf && isAdminRole && isSelected;

                        const label =
                            role.label?.[i18n.language] ||
                            role.label?.en ||
                            role.label?.ar ||
                            role.name;

                        return (
                            <Tooltip
                                key={role.id}
                                title={
                                    lockedSelf
                                        ? (isArabic
                                            ? "لا يمكنك إزالة دور المسؤول من حسابك"
                                            : "Cannot remove admin role from yourself")
                                        : role.name
                                }
                            >
                                <Chip
                                    icon={lockedSelf ? <Lock sx={{ fontSize: 14 }} /> : undefined}
                                    label={label}
                                    onClick={lockedSelf ? undefined : () => toggle(role)}
                                    color={isSelected ? "primary" : "default"}
                                    variant={isSelected ? "tonal" : "outlined"}
                                    sx={{
                                        fontWeight: "bold",
                                        borderRadius: "8px",
                                        py: 2,
                                        px: 0.5,
                                        cursor: lockedSelf ? "not-allowed" : "pointer",
                                        opacity: lockedSelf ? 0.7 : 1,
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Stack>
            )}
        </Paper>
    );
};

export default CustomerRolesTab;
